import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { AuthApiService } from '../../_core/_services/auth-api.service';
import { AuthService } from '../../_core/_services/auth.service';
import { NotificationService } from '../../_core/_services/notification.service';
import { AccountPinSecurity } from '../../_core/_model/users';


type SetupPinForm = FormGroup<{
  currentPassword: FormControl<string>;
  pin: FormControl<string>;
  confirmPin: FormControl<string>;
  allowPinLogin: FormControl<boolean>;
  requirePinForDeposits: FormControl<boolean>;
  requirePinForWithdrawals: FormControl<boolean>;
}>;

type PinSettingsForm = FormGroup<{
  pin: FormControl<string>;
  allowPinLogin: FormControl<boolean>;
  requirePinForDeposits: FormControl<boolean>;
  requirePinForWithdrawals: FormControl<boolean>;
}>;

type ChangePinForm = FormGroup<{
  currentPin: FormControl<string>;
  newPin: FormControl<string>;
  confirmNewPin: FormControl<string>;
}>;

type ResetPinForm = FormGroup<{
  currentPassword: FormControl<string>;
  newPin: FormControl<string>;
  confirmNewPin: FormControl<string>;
  totpCode: FormControl<string>;
}>;

type DisablePinForm = FormGroup<{
  pin: FormControl<string>;
}>;

@Component({
  selector: 'app-account-security',
  standalone: false,
  templateUrl: './account-security.html',
  styleUrl: './account-security.css'
})
export class AccountSecurity implements OnInit {
  loading = true;
  saving = false;
  error = '';
  settings: AccountPinSecurity | null = null;
  otpEnabled = false;

  setupForm!: SetupPinForm;
  settingsForm!: PinSettingsForm;
  changeForm!: ChangePinForm;
  resetForm!: ResetPinForm;
  disableForm!: DisablePinForm;

  constructor(
    private fb: FormBuilder,
    private api: AuthApiService,
    private auth: AuthService,
    private notify: NotificationService
  ) {
    this.setupForm = this.fb.nonNullable.group({
    currentPassword: ['', Validators.required],
    pin: ['', [Validators.required, Validators.pattern(/^\d{6}$/)]],
    confirmPin: ['', [Validators.required, Validators.pattern(/^\d{6}$/)]],
    allowPinLogin: [false],
    requirePinForDeposits: [false],
    requirePinForWithdrawals: [false]
  });

    this.settingsForm = this.fb.nonNullable.group({
    pin: ['', [Validators.required, Validators.pattern(/^\d{6}$/)]],
    allowPinLogin: [false],
    requirePinForDeposits: [false],
    requirePinForWithdrawals: [false]
  });

    this.changeForm = this.fb.nonNullable.group({
    currentPin: ['', [Validators.required, Validators.pattern(/^\d{6}$/)]],
    newPin: ['', [Validators.required, Validators.pattern(/^\d{6}$/)]],
    confirmNewPin: ['', [Validators.required, Validators.pattern(/^\d{6}$/)]]
  });

    this.resetForm = this.fb.nonNullable.group({
    currentPassword: ['', Validators.required],
    newPin: ['', [Validators.required, Validators.pattern(/^\d{6}$/)]],
    confirmNewPin: ['', [Validators.required, Validators.pattern(/^\d{6}$/)]],
    totpCode: ['']
  });

    this.disableForm = this.fb.nonNullable.group({
    pin: ['', [Validators.required, Validators.pattern(/^\d{6}$/)]]
  });
  }

  ngOnInit(): void {
    this.auth.getCurrentUser().subscribe(user => this.otpEnabled = !!user?.otp?.otpEnabled);
    this.load();
  }

  load(): void {
    this.loading = true;
    this.api.getAccountPinSettings().subscribe({
      next: res => {
        this.settings = res.data;
        this.patchSettings();
      },
      error: err => {
        this.error = this.message(err, 'Failed to load security settings.');
        this.loading = false;
      },
      complete: () => this.loading = false
    });
  }

  setup(): void {
    if (this.setupForm.invalid || this.saving) return;
    const value = this.setupForm.getRawValue();
    if (value.pin !== value.confirmPin) {
      this.notify.showError('PIN confirmation does not match.', 'Account PIN');
      return;
    }

    this.run(this.api.setupAccountPin(value), 'Account PIN configured successfully.', () => {
      this.setupForm.reset({
        currentPassword: '', pin: '', confirmPin: '',
        allowPinLogin: false, requirePinForDeposits: false, requirePinForWithdrawals: false
      });
    });
  }

  saveSettings(): void {
    if (this.settingsForm.invalid || this.saving) return;
    this.run(this.api.updateAccountPinSettings(this.settingsForm.getRawValue()), 'PIN preferences updated.', () => {
      this.settingsForm.controls.pin.reset('');
    });
  }

  changePin(): void {
    if (this.changeForm.invalid || this.saving) return;
    const value = this.changeForm.getRawValue();
    if (value.newPin !== value.confirmNewPin) {
      this.notify.showError('New PIN confirmation does not match.', 'Account PIN');
      return;
    }

    this.run(this.api.changeAccountPin(value), 'Account PIN changed.', () => this.changeForm.reset());
  }

  resetPin(): void {
    if (this.resetForm.invalid || this.saving) return;
    const value = this.resetForm.getRawValue();
    if (value.newPin !== value.confirmNewPin) {
      this.notify.showError('New PIN confirmation does not match.', 'Account PIN');
      return;
    }
    if (this.otpEnabled && !/^\d{6}$/.test(value.totpCode || '')) {
      this.notify.showError('Enter your six-digit authenticator code.', 'Account PIN');
      return;
    }

    this.run(this.api.resetAccountPin({
      ...value,
      totpCode: value.totpCode || null
    }), 'Account PIN reset. Turn on the uses you want after reviewing the settings.', () => this.resetForm.reset());
  }

  disablePin(): void {
    if (this.disableForm.invalid || this.saving) return;
    if (!confirm('Disable the account PIN and all PIN protections?')) return;

    this.saving = true;
    this.api.disableAccountPin(this.disableForm.getRawValue().pin).subscribe({
      next: () => {
        this.notify.showSuccess('Account PIN disabled.', 'Account Security');
        this.disableForm.reset();
        this.refreshUserAndLoad();
      },
      error: err => {
        this.notify.showError(this.message(err, 'Failed to disable account PIN.'), 'Account Security');
        this.saving = false;
      },
      complete: () => this.saving = false
    });
  }

  private run(request$: any, success: string, after?: () => void): void {
    this.saving = true;
    request$.subscribe({
      next: (res: any) => {
        this.settings = res.data;
        this.patchSettings();
        after?.();
        this.notify.showSuccess(success, 'Account Security');
        this.refreshUser();
      },
      error: (err: any) => {
        this.notify.showError(this.message(err, 'Security update failed.'), 'Account Security');
        this.saving = false;
      },
      complete: () => this.saving = false
    });
  }

  private patchSettings(): void {
    if (!this.settings) return;
    this.settingsForm.patchValue({
      allowPinLogin: this.settings.allowPinLogin,
      requirePinForDeposits: this.settings.requirePinForDeposits,
      requirePinForWithdrawals: this.settings.requirePinForWithdrawals
    });
  }

  private refreshUserAndLoad(): void {
    this.refreshUser();
    this.load();
  }

  private refreshUser(): void {
    this.auth.clearUser();
    this.auth.getCurrentUser().subscribe();
  }

  private message(err: any, fallback: string): string {
    return err?.error?.message || err?.error?.Error?.Details || err?.error?.error?.details || fallback;
  }
}
